import { Injectable, inject } from '@angular/core';
import {
  Auth, // Keep these for now as methods are still present
  FacebookAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  reload,
  sendPasswordResetEmail,
  signInWithEmailAndPassword, // Keep these for now as methods are still present
  signInWithPopup, // Keep these for now as methods are still present
  signOut,
  updateEmail,
  updateProfile,
  user,
} from '@angular/fire/auth';
import { serverTimestamp, getDocs, collection, query, where } from '@angular/fire/firestore';
import { Observable, firstValueFrom, of, switchMap } from 'rxjs';
import { UserProfile } from '../models/models';
import { FirestoreService } from './firestore.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestoreService = inject(FirestoreService);

  user$ = user(this.auth);

  /**
   * Get the current authenticated user (Promise based).
   * Awaits authStateReady() so the Firebase session is fully restored from
   * IndexedDB before we read currentUser — prevents a null race on page load.
   */
  async getCurrentUser() {
    await this.auth.authStateReady();
    return this.auth.currentUser;
  }

  async reloadCurrentUser() {
    await this.auth.authStateReady();
    if (this.auth.currentUser) {
      await reload(this.auth.currentUser);
    }
    return this.auth.currentUser;
  }

  async isCurrentUserEmailVerified(forceReload = true): Promise<boolean> {
    const currentUser = forceReload
      ? await this.reloadCurrentUser()
      : await this.getCurrentUser();
    return !!currentUser?.emailVerified;
  }

  async syncCurrentUserVerificationState(): Promise<void> {
    const currentUser = await this.reloadCurrentUser();

    if (!currentUser) {
      return;
    }

    await this.firestoreService.updateDoc('user_profiles', currentUser.uid, {
      email: currentUser.email,
      emailVerified: currentUser.emailVerified,
      updatedAt: serverTimestamp(),
    });
  }

  async updateCurrentUserEmail(newEmail: string): Promise<void> {
    const currentUser = await this.getCurrentUser();

    if (!currentUser) {
      throw new Error('AUTH_USER_MISSING');
    }

    await updateEmail(currentUser, newEmail);
    await this.firestoreService.updateDoc('user_profiles', currentUser.uid, {
      email: newEmail,
      emailVerified: false,
      updatedAt: serverTimestamp(),
    });
    await this.reloadCurrentUser();
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, pass: string) {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const credential = await signInWithEmailAndPassword(this.auth, normalizedEmail, pass);

      return credential;
    } catch (error: any) {
      console.error('AuthService: signInWithEmail failed', { 
        email: normalizedEmail, 
        code: error.code, 
        message: error.message 
      });
      throw error;
    }
  }

  /**
   * Register a new user with email and password
   */
  async registerWithEmail(
    email: string,
    pass: string,
    name: string,
    phone: string,
  ) {
    const normalizedEmail = email.trim().toLowerCase();
    const credential = await createUserWithEmailAndPassword(
      this.auth,
      normalizedEmail,
      pass,
    );

    // Update Firebase Auth profile with display name
    await updateProfile(credential.user, { displayName: name });

    // Store additional info in Firestore
    await this.firestoreService.setDoc('user_profiles', credential.user.uid, {
      uid: credential.user.uid,
      fullName: name,
      email: normalizedEmail,
      emailVerified: credential.user.emailVerified,
      phoneNumber: phone,
      role: 'user',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return credential;
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(this.auth, provider);

    await this.upsertSocialProfile(credential.user);

    return credential;
  }

  /**
   * Sign in with Facebook
   */
  async signInWithFacebook() {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    provider.addScope('public_profile');
    const credential = await signInWithPopup(this.auth, provider);

    await this.upsertSocialProfile(credential.user);

    return credential;
  }

  /**
   * Sign in via Instagram (Meta unified login).
   * Instagram's own OAuth API was shut down December 2024.
   * This uses FacebookAuthProvider with instagram_basic scope — the only
   * officially supported path. The popup shows Meta/Facebook login where users
   * whose Instagram is linked to a Facebook account can authenticate.
   */
  async signInWithInstagram() {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    provider.addScope('public_profile');
    provider.addScope('instagram_basic');
    const credential = await signInWithPopup(this.auth, provider);

    await this.upsertSocialProfile(credential.user);

    return credential;
  }

  private async upsertSocialProfile(
    socialUser: {
      uid: string;
      displayName: string | null;
      email: string | null;
      emailVerified: boolean;
      phoneNumber: string | null;
      photoURL: string | null;
    },
  ): Promise<void> {
    const profilePatch: Partial<UserProfile> = {
      uid: socialUser.uid,
      email: socialUser.email ? socialUser.email.trim().toLowerCase() : null,
      emailVerified: socialUser.emailVerified,
      lastLogin: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (socialUser.displayName?.trim()) {
      profilePatch.displayName = socialUser.displayName.trim();
    }
    if (socialUser.phoneNumber?.trim()) {
      profilePatch.phoneNumber = socialUser.phoneNumber.trim();
    }
    if (socialUser.photoURL) {
      profilePatch.photoURL = socialUser.photoURL;
    }

    await this.firestoreService.setDoc(
      'user_profiles',
      socialUser.uid,
      profilePatch as UserProfile,
      { merge: true },
    );
  }

  /**
   * Get user profile data from Firestore
   */
  getUserProfile(uid: string): Observable<UserProfile | null> {
    return this.firestoreService.getDoc<UserProfile>(
      'user_profiles',
      uid,
    ) as Observable<UserProfile | null>;
  }

  /**
   * Stream of combined Auth user and Firestore profile
   */
  userProfile$ = this.user$.pipe(
    switchMap((authUser) => {
      if (!authUser) return of(null);
      return this.getUserProfile(authUser.uid);
    }),
  );

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string) {
    return await sendPasswordResetEmail(this.auth, email);
  }

  /**
   * Check if a user with the given email exists in Firestore user_profiles
   */
  async checkUserExists(email: string): Promise<boolean> {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const q = query(
        collection(this.firestoreService.getFirestoreInstance(), 'user_profiles'),
        where('email', '==', normalizedEmail)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking user existence:', error);
      // In case of error, we can't be sure, so we could return false to be safe
      // or return true to allow the standard Firebase flow.
      // But looking up Firestore is more reliable for our check.
      return false;
    }
  }

  /**
   * Sign out
   */
  async logout() {
    return await signOut(this.auth);
  }
}
